import React, { useState, useEffect } from 'react';
import { Globe, Search, Shield, ArrowRight, Hash, Clock, Copy, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'dns', name: 'Verificador de DNS', path: '/domains/dns' },
    { id: 'availability', name: 'Disponibilidade de Domínio', path: '/domains/availability' },
    { id: 'ssl', name: 'Verificador de SSL', path: '/domains/ssl' },
    { id: 'redirect', name: 'Verificador de Redirecionamento', path: '/domains/redirect' },
    { id: 'subdomains', name: 'Gerador de Subdomínios', path: '/domains/subdomains' },
    { id: 'response', name: 'Tempo de Resposta', path: '/domains/response' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Domínios', tool: match ? match.name : null };
}

const Domains = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'dns', name: 'Verificador de DNS', icon: Search, path: '/domains/dns', description: 'Consulta de registros', status: 'active' },
    { id: 'availability', name: 'Disponibilidade de Domínio', icon: Globe, path: '/domains/availability', description: 'Verificar disponibilidade', status: 'active' },
    { id: 'redirect', name: 'Verificador de Redirecionamento', icon: ArrowRight, path: '/domains/redirect', description: 'Rastreamento de redirecionamentos', status: 'active' },
    { id: 'subdomains', name: 'Gerador de Subdomínios', icon: Hash, path: '/domains/subdomains', description: 'Lista de subdomínios', status: 'active' },
    { id: 'response', name: 'Tempo de Resposta', icon: Clock, path: '/domains/response', description: 'Teste de velocidade', status: 'active' }
  ];

  // Redirecionar /domains para /domains/dns
  useEffect(() => {
    if (location.pathname === '/domains') {
      navigate('/domains/dns');
    }
  }, [location.pathname, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Domínios
        </h1>
        <p className="text-gray-600">
          Verificadores de DNS, SSL, disponibilidade e redirecionamentos
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="dns" element={<DNSLookup />} />
        <Route path="availability" element={<DomainAvailability />} />
        <Route path="redirect" element={<RedirectChecker />} />
        <Route path="subdomains" element={<SubdomainGenerator />} />
        <Route path="response" element={<ResponseTime />} />
      </Routes>
    </div>
  );
};

// Componente Verificador de DNS
const DNSLookup = () => {
  const [domain, setDomain] = useState('');
  const [dnsResults, setDnsResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkDNS = async () => {
    if (!domain.trim()) {
      toast.error('Digite um domínio');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/domains/dns-checker', { domain });
      setDnsResults(data);
    } catch (error) {
      toast.error('Erro ao verificar DNS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Domínio
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="exemplo.com"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
            />
            <button
              onClick={checkDNS}
              disabled={loading}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Verificar DNS'}
            </button>
          </div>
        </div>

        {dnsResults && dnsResults.results && (
          <div className="space-y-4">
            {Object.entries(dnsResults.results).map(([recordType, records]) => (
              <div key={recordType} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 capitalize">
                  {recordType}
                </h4>
                {Array.isArray(records) && records.length > 0 ? (
                  <div className="space-y-1">
                    {records.map((record, index) => (
                      <div key={index} className="text-sm font-mono text-gray-900">
                        {typeof record === 'object' ? JSON.stringify(record) : record}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    {records && records.error ? records.error : 'Nenhum registro encontrado'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Disponibilidade de Domínio
const DomainAvailability = () => {
  const [domain, setDomain] = useState('');
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkAvailability = async () => {
    if (!domain.trim()) {
      toast.error('Digite um domínio');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/domains/domain-availability', { domain });
      setAvailability(data);
    } catch (error) {
      toast.error('Erro ao verificar disponibilidade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Domínio
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="exemplo.com"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
            />
            <button
              onClick={checkAvailability}
              disabled={loading}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </div>

        {availability && (
          <div className={`text-center p-6 rounded-lg ${
            availability.available 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center justify-center space-x-2 mb-2">
              {availability.available ? (
                <Check className="text-green-500" size={24} />
              ) : (
                <X className="text-red-500" size={24} />
              )}
              <span className="text-xl font-bold">
                {availability.available ? 'Disponível' : 'Indisponível'}
              </span>
            </div>
            <div className="text-sm">
              {availability.available 
                ? 'Este domínio está disponível para registro'
                : 'Este domínio já está registrado'
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Verificador de Redirecionamento
const RedirectChecker = () => {
  const [url, setUrl] = useState('');
  const [redirects, setRedirects] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkRedirects = async () => {
    if (!url.trim()) {
      toast.error('Digite uma URL');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/domains/redirect-checker', { url });
      setRedirects(data);
    } catch (error) {
      toast.error('Erro ao verificar redirecionamentos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            URL
          </label>
          <div className="flex space-x-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://exemplo.com"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
            />
            <button
              onClick={checkRedirects}
              disabled={loading}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </div>

        {redirects && Array.isArray(redirects) && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-lg font-medium text-gray-900">
                {redirects.length} redirecionamento(s) encontrado(s)
              </div>
            </div>

            <div className="space-y-3">
              {redirects.map((redirect, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {redirect.status}
                    </span>
                    <ArrowRight className="text-gray-400" size={16} />
                  </div>
                  <div className="text-sm text-gray-600 break-all">
                    {redirect.url}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de Subdomínios
const SubdomainGenerator = () => {
  const [domain, setDomain] = useState('');
  const [subdomains, setSubdomains] = useState([]);
  const [loading, setLoading] = useState(false);

  const commonSubdomains = [
    'www', 'mail', 'ftp', 'admin', 'blog', 'api', 'cdn', 'dev', 'test', 'staging',
    'app', 'mobile', 'shop', 'store', 'support', 'help', 'docs', 'forum', 'news',
    'media', 'images', 'files', 'download', 'upload', 'secure', 'login', 'dashboard'
  ];

  const generateSubdomains = async () => {
    if (!domain.trim()) {
      toast.error('Digite um domínio');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/domains/subdomain-generator', { domain, subdomains: commonSubdomains });
      setSubdomains(data.subdomains || []);
    } catch (error) {
      toast.error('Erro ao gerar subdomínios');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Domínio Base
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="exemplo.com"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
            />
            <button
              onClick={generateSubdomains}
              disabled={loading}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Gerando...' : 'Gerar Subdomínios'}
            </button>
          </div>
        </div>

        {subdomains.length > 0 && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-lg font-medium text-gray-900">
                {subdomains.length} subdomínio(s) encontrado(s)
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
              {subdomains.map((subdomain, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-mono text-gray-900">
                      {subdomain.subdomain}.{domain}
                    </div>
                    <div className="flex items-center space-x-2">
                      {subdomain.exists ? (
                        <Check className="text-green-500" size={16} />
                      ) : (
                        <X className="text-red-500" size={16} />
                      )}
                      <button
                        onClick={() => copyToClipboard(`${subdomain.subdomain}.${domain}`)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Tempo de Resposta
const ResponseTime = () => {
  const [url, setUrl] = useState('');
  const [responseTime, setResponseTime] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkResponseTime = async () => {
    if (!url.trim()) {
      toast.error('Digite uma URL');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/domains/response-time', { url });
      setResponseTime(data);
    } catch (error) {
      toast.error('Erro ao verificar tempo de resposta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            URL
          </label>
          <div className="flex space-x-2">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://exemplo.com"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
            />
            <button
              onClick={checkResponseTime}
              disabled={loading}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </div>

        {responseTime && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {responseTime.time}ms
              </div>
              <div className="text-sm text-gray-600">Tempo de Resposta</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {responseTime.status}
                </div>
                <div className="text-sm text-gray-600">Status HTTP</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {responseTime.size}
                </div>
                <div className="text-sm text-gray-600">Tamanho (bytes)</div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">URL Final</h4>
              <div className="text-sm text-gray-600 break-all">
                {responseTime.finalUrl}
              </div>
            </div>

            {/* Novas informações detalhadas */}
            <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-full">
              <div>
                <div className="text-xs text-gray-500">Data/Hora do Teste</div>
                <div className="text-sm text-gray-900">{responseTime.datetime}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Mensagem</div>
                <div className="text-sm text-gray-900">{responseTime.statusMessage}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Tipo de Requisição</div>
                <div className="text-sm text-gray-900">{responseTime.requestType}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">User-Agent</div>
                <div className="text-sm text-gray-900 break-all">{responseTime.userAgent}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Descrição</div>
                <div className="text-sm text-gray-900">{responseTime.description}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Domains; 